import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { fetchPayment, newIdempotencyKey, refundPayment } from "../services/paymentService";
import { labelFor } from "../services/enumLabels";

const REASONS = [
  "cancellation",
  "no_show_waived",
  "service_issue",
  "overcharge",
  "duplicate",
  "goodwill",
  "other",
];

/**
 * Giving money back.
 *
 * ## The policy is shown before anybody types a number
 *
 * The API returns a quote alongside the payment: what is left, what the
 * booking's own cancellation terms say the customer forfeits, and what that
 * leaves refundable. All three are on screen before the amount box, so
 * exceeding the policy is a deliberate act rather than a 422 at submit time.
 *
 * Whether it is *allowed* is the server's call, not this component's. The
 * amount is prefilled with what policy permits and the warning appears above
 * it, but the refusal comes from the API — hiding the input would only teach
 * people to work around the screen.
 *
 * ## One key per attempt
 *
 * Generated when the modal opens and reused across retries, so a dropped
 * connection cannot refund twice. A fresh attempt takes a fresh key.
 */
export default function RefundModal({ payment, open, onClose, onRefunded }) {
  const { t } = useTranslation();

  const [quote, setQuote] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("cancellation");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  useEffect(() => {
    if (!open || !payment) return;

    setError("");
    setFieldErrors({});
    setNote("");
    setReason("cancellation");
    setIdempotencyKey(newIdempotencyKey());
    setLoading(true);

    fetchPayment(payment.id)
      .then(({ refundQuote }) => {
        setQuote(refundQuote);
        // Prefilled with what the policy allows, not with the whole amount:
        // the common case is the correct one, and the other needs a decision.
        setAmount(refundQuote?.policy_refund ?? payment.refundable ?? "");
      })
      .catch((err) => setError(apiErrorMessage(err, t("transactions.quoteFailed"))))
      .finally(() => setLoading(false));
  }, [open, payment, t]);

  if (!open || !payment) return null;

  const exceedsPolicy = quote && Number(amount) > Number(quote.policy_refund);
  const symbol = payment.currency?.symbol ?? "";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setFieldErrors({});

    try {
      const { refund } = await refundPayment(payment.id, { amount, reason, note }, idempotencyKey);
      onRefunded(refund);
      onClose();
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("transactions.refundFailed")));
      // A refusal is a genuinely new attempt once the amount changes, so the
      // key is retired rather than reused against a different request.
      setIdempotencyKey(newIdempotencyKey());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="card my-8 w-full max-w-lg p-5">
        <h2 className="mb-1 text-lg font-bold">{t("transactions.refundTitle")}</h2>
        <p className="mb-4 text-sm text-ink-2">
          {t("transactions.refundSubtitle", { reference: payment.reference })}
        </p>

        {loading ? (
          <p className="py-8 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {/* What the booking's own terms say, before anybody types. */}
            {quote && (
              <div className="rounded-lg border border-line p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-2">{t("transactions.quote.refundable")}</span>
                  <span className="font-medium">
                    {symbol} {quote.refundable}
                  </span>
                </div>
                <div className="mt-1 flex justify-between">
                  <span className="text-ink-2">{t("transactions.quote.policyFee")}</span>
                  <span>
                    {symbol} {quote.policy_fee}
                  </span>
                </div>
                <div className="mt-1 flex justify-between border-t border-line pt-1">
                  <span className="text-ink-2">{t("transactions.quote.policyRefund")}</span>
                  <span className="font-medium">
                    {symbol} {quote.policy_refund}
                  </span>
                </div>
                {quote.inside_window && (
                  <p className="mt-2 text-xs text-warning-text">
                    {t("transactions.quote.insideWindow")}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="field-label" htmlFor="refund-amount">
                {t("transactions.refundAmount")} <span className="text-danger-text">*</span>
              </label>
              <input
                id="refund-amount"
                type="number"
                step="0.01"
                min="0"
                className="field-input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              {fieldErrors.amount && <p className="field-error">{fieldErrors.amount[0]}</p>}
            </div>

            {/* Said before submitting, not after being refused. */}
            {exceedsPolicy && (
              <div className="rounded-lg border border-warning-line bg-warning-wash px-3 py-2 text-sm text-warning-text">
                {t("transactions.exceedsPolicy", { allowed: quote.policy_refund })}
              </div>
            )}

            {/* Only a full refund undoes a reward: it is not divisible. */}
            {payment.loyalty_redemption_id && (
              <p className="rounded-lg border border-line bg-raised px-3 py-2 text-xs text-ink-2">
                {t("transactions.rewardReturnHint")}
              </p>
            )}

            <div>
              <label className="field-label" htmlFor="refund-reason">
                {t("transactions.refundReason")} <span className="text-danger-text">*</span>
              </label>
              <select
                id="refund-reason"
                className="field-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                {REASONS.map((option) => (
                  <option key={option} value={option}>
                    {labelFor("refund_reason", option)}
                  </option>
                ))}
              </select>
              {fieldErrors.reason && <p className="field-error">{fieldErrors.reason[0]}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="refund-note">
                {t("transactions.refundNote")}
              </label>
              <textarea
                id="refund-note"
                rows={2}
                className="field-input"
                placeholder={t("transactions.refundNotePlaceholder")}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            {error && <p className="field-error">{error}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={saving || !amount}
                className="rounded-lg bg-danger px-4 py-2 text-dense font-semibold text-danger-on transition
                  hover:bg-danger-text disabled:cursor-not-allowed disabled:border disabled:border-line-strong disabled:bg-raised disabled:text-ink-2"
              >
                {saving ? t("common.saving") : t("transactions.refund")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
